import type { PaddleCheckout } from '@/@types/trackheros'

/**
 * Paddle.js, loaded on first use and never before.
 *
 * Only a Paddle deployment needs it, and only on the click that opens a
 * checkout — so it is injected then rather than shipped in the bundle or put
 * in index.html, where every page of a Stripe deployment would fetch a third
 * party's script for nothing.
 *
 * The overlay is opened on a transaction our API created. The browser never
 * names a price, a customer or an account: those are on the transaction
 * already, which is what lets the server trust the subscription it produces.
 */

const SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js'

type PaddleEvent = { name?: string; data?: unknown }

type PaddleGlobal = {
    Environment: { set: (environment: string) => void }
    Initialize: (options: {
        token: string
        eventCallback?: (event: PaddleEvent) => void
    }) => void
    Checkout: {
        open: (options: {
            transactionId: string
            settings?: Record<string, unknown>
        }) => void
        close: () => void
    }
}

declare global {
    interface Window {
        Paddle?: PaddleGlobal
    }
}

export type CheckoutOutcome = 'completed' | 'closed'

let loading: Promise<PaddleGlobal> | null = null
let initialisedFor = ''
// Paddle.js takes one event callback, at Initialize. Each open checkout
// swaps in its own listener here instead of re-initialising.
let listener: ((event: PaddleEvent) => void) | null = null

function loadScript(): Promise<PaddleGlobal> {
    if (window.Paddle) return Promise.resolve(window.Paddle)
    if (loading) return loading
    loading = new Promise<PaddleGlobal>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = SCRIPT_URL
        script.async = true
        script.onload = () =>
            window.Paddle
                ? resolve(window.Paddle)
                : reject(new Error('Paddle.js loaded without defining Paddle'))
        script.onerror = () => {
            loading = null
            reject(new Error('Could not load Paddle.js'))
        }
        document.head.appendChild(script)
    })
    return loading
}

async function ready(checkout: PaddleCheckout): Promise<PaddleGlobal> {
    const Paddle = await loadScript()
    const key = `${checkout.environment}:${checkout.client_token}`
    if (initialisedFor !== key) {
        // Must precede Initialize. A sandbox token against the live
        // environment fails with an error that does not say so.
        if (checkout.environment === 'sandbox')
            Paddle.Environment.set('sandbox')
        Paddle.Initialize({
            token: checkout.client_token,
            eventCallback: (event) => listener?.(event),
        })
        initialisedFor = key
    }
    return Paddle
}

/**
 * Open Paddle's overlay on a transaction and resolve when the customer is
 * done with it.
 *
 * `completed` means Paddle took the payment — **not** that the plan is
 * active. That happens when Paddle's webhook reaches our API, usually a few
 * seconds later, so callers revalidate rather than assume.
 */
export async function openPaddleCheckout(
    checkout: PaddleCheckout,
    options: { locale?: string } = {},
): Promise<CheckoutOutcome> {
    const Paddle = await ready(checkout)
    return new Promise<CheckoutOutcome>((resolve) => {
        let settled = false
        const settle = (outcome: CheckoutOutcome) => {
            if (settled) return
            settled = true
            listener = null
            resolve(outcome)
        }
        listener = (event) => {
            if (event.name === 'checkout.completed') {
                // Leave Paddle's own confirmation on screen for a moment,
                // then hand control back to the page.
                window.setTimeout(() => Paddle.Checkout.close(), 1500)
                settle('completed')
            } else if (event.name === 'checkout.closed') {
                settle('closed')
            }
        }
        Paddle.Checkout.open({
            transactionId: checkout.transaction_id,
            settings: {
                displayMode: 'overlay',
                // The customer was set server-side, from the account's billing
                // email. Letting them switch it here would detach the
                // subscription from the account that is paying.
                allowLogout: false,
                ...(options.locale ? { locale: options.locale } : {}),
            },
        })
    })
}

/**
 * Open a URL we only learn after an await, without the popup blocker eating
 * it: the window is opened inside the click, then pointed at the URL.
 */
export async function openInNewTab(getUrl: () => Promise<string>) {
    const opened = window.open('', '_blank')
    try {
        const url = await getUrl()
        if (opened) {
            opened.opener = null
            opened.location.href = url
        } else {
            window.location.href = url
        }
    } catch (error) {
        opened?.close()
        throw error
    }
}
